import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useWizard } from 'react-use-wizard';
import styled from 'styled-components';
import tw from 'twin.macro';

import CountDown from '@app/components/count-down';

type WizardFooterPropTypes = {
  loading: boolean;
  enablePay: boolean;
  disablePay: boolean;
  nextLabel?: string;
  disableNext?: boolean | ((currentStep: number) => boolean);
  handleNext: (next: () => void, currentStep: number) => void;
  handlePrevious: (previous: () => void, currentStep: number) => void;
  handlePay: () => void;
};

const WizardFooterStyled = styled.div`
  ${tw`flex flex-row justify-center my-12 justify-between md:justify-center`}

  button {
    width: 250px;
    font-weight: bold;
    margin-left: 0;
  }
`;

export default function WizardFooter({
  disableNext = false,
  loading,
  enablePay,
  disablePay = false,
  nextLabel = 'Next',
  handleNext,
  handlePrevious,
  handlePay,
}: WizardFooterPropTypes): JSX.Element {
  const { isLoading, isFirstStep, isLastStep, nextStep, previousStep, activeStep } = useWizard();
  const [timer, setTimer] = useState(0);
  const [previousLoading, setPreviousLoading] = useState(false);
  const onNext = () => {
    handleNext(nextStep, activeStep + 1);
  };
  const onPrevious = async () => {
    setPreviousLoading(true);
    await handlePrevious(previousStep, activeStep - 1);
    setPreviousLoading(false);
  };
  const isDisabled = typeof disableNext === 'boolean' ? disableNext : disableNext(activeStep);
  const onPay = () => {
    if (!enablePay) {
      toast('Please agree to our terms and conditions to continue.', {
        type: 'error',
        autoClose: 3000,
      });
    } else {
      // connect to payment gateway
      handlePay();
    }
  };
  useEffect(() => {
    if (activeStep === 3) {
      const currentTime = new Date();
      const seconds = 120;
      const currentTimestamp = currentTime.setSeconds(currentTime.getSeconds() + seconds);
      setTimer(currentTimestamp);
    } else {
      setTimer(0);
    }
  }, [activeStep]);
  return (
    <WizardFooterStyled>
      <button
        type="button"
        className="btn btn--primary-outline border-primary-500 mx-5"
        onClick={onPrevious}
        disabled={isFirstStep || isLoading || loading || previousLoading}>
        Previous
      </button>
      {isLastStep ? (
        <button
          type="button"
          className="btn btn--primary"
          onClick={onPay}
          disabled={loading || disablePay}>
          Card payments {activeStep >= 3 && timer ? <CountDown expiryTimestamp={timer} /> : null}
        </button>
      ) : (
        <button
          type="submit"
          className="btn btn--primary"
          onClick={onNext}
          disabled={isLastStep || isLoading || isDisabled || loading}>
          {nextLabel}
        </button>
      )}
    </WizardFooterStyled>
  );
}
